import Link from "next/link";
import type { ReactNode } from "react";
import type { TestDefinition } from "@test-platform/shared";
import type { Locale } from "@/lib/i18n";
import { toLocalizedPath } from "@/lib/i18n";
import Icon, { type IconName } from "./Icon";

const TEST_ACCENTS: Record<string, { icon: IconName; gradient: string }> = {
  mbti: {
    icon: "brain",
    gradient: "from-fuchsia-400 via-pink-400 to-orange-300",
  },
  "teto-egen": {
    icon: "flame",
    gradient: "from-rose-500 via-orange-400 to-amber-300",
  },
  zodiac: {
    icon: "sparkles",
    gradient: "from-indigo-400 via-violet-400 to-sky-300",
  },
  "profile-photo-vibe": {
    icon: "camera",
    gradient: "from-slate-900 via-indigo-700 to-pink-500",
  },
  "video-scene-quiz": {
    icon: "video",
    gradient: "from-violet-500 via-fuchsia-500 to-pink-400",
  },
  "visual-clue-quiz": {
    icon: "image",
    gradient: "from-cyan-500 via-sky-400 to-indigo-400",
  },
  "scenario-choice-quiz": {
    icon: "shapes",
    gradient: "from-amber-500 via-orange-400 to-rose-400",
  },
  "love-conversation-chemistry": {
    icon: "message-circle-heart",
    gradient: "from-rose-500 to-pink-600",
  },
  "date-tempo-style": {
    icon: "calendar-heart",
    gradient: "from-teal-600 to-cyan-600",
  },
  "ideal-first-impression": {
    icon: "heart",
    gradient: "from-pink-600 to-violet-600",
  },
  "ideal-cup": { icon: "crown", gradient: "from-amber-500 to-orange-600" },
  "k-drama-hero": { icon: "video", gradient: "from-indigo-600 to-violet-600" },
};

const FALLBACK_ACCENT: { icon: IconName; gradient: string } = {
  icon: "sparkles",
  gradient: "from-violet-400 via-sky-400 to-emerald-300",
};

type Props = {
  test: Pick<TestDefinition, "id" | "title" | "description" | "createdAt"> &
    Partial<Pick<TestDefinition, "questions">> & {
      isNew?: boolean;
      questionCount?: number;
    };
  index?: number;
  badge?: ReactNode;
  locale?: Locale;
};

export default function TestCard({
  test,
  index = 0,
  badge,
  locale = "ko",
}: Props) {
  const accent = TEST_ACCENTS[test.id] ?? FALLBACK_ACCENT;
  const isPhotoTest = test.id === "profile-photo-vibe";
  const isNew = test.isNew ?? false;
  const questionCount = test.questionCount ?? test.questions?.length ?? 0;
  const href = toLocalizedPath(`/tests/${test.id}`, locale);
  const copy = {
    newLabel: locale === "ko" ? "새 테스트" : "New test",
    photo: locale === "ko" ? "AI 사진 분석" : "AI photo analysis",
    questions:
      locale === "ko" ? `${questionCount}문항` : `${questionCount} questions`,
    minutes:
      locale === "ko"
        ? `약 ${Math.max(1, Math.round(questionCount / 6))}분`
        : `About ${Math.max(1, Math.round(questionCount / 6))} min`,
    photoMinutes: locale === "ko" ? "약 1분" : "About 1 min",
  };
  return (
    <Link
      href={href}
      prefetch={false}
      className="card group flex items-center gap-4 animate-floatIn focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div
        className={`relative grid size-16 shrink-0 place-items-center rounded-2xl bg-gradient-to-br ${accent.gradient} text-white shadow-soft`}
        aria-hidden
      >
        <Icon name={accent.icon} size={32} strokeWidth={1.75} />
        {badge ? (
          <span className="absolute -left-1 -top-1 grid size-6 place-items-center rounded-full bg-white text-xs font-display text-brand-700 shadow-soft ring-1 ring-brand-100">
            {badge}
          </span>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h3 className="font-display text-base leading-6 text-slate-900">
          {test.title}
          {isNew ? (
            <span
              className="ml-1.5 inline-flex items-center rounded-full bg-rose-500 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wide text-white"
              aria-label={copy.newLabel}
            >
              N
            </span>
          ) : null}
        </h3>
        <p className="line-clamp-1 text-sm leading-6 text-slate-600">
          {test.description}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-800 ring-1 ring-brand-100">
            {isPhotoTest ? copy.photo : copy.questions}
          </span>
          <span className="text-sm text-slate-600">
            {isPhotoTest ? copy.photoMinutes : copy.minutes}
          </span>
        </div>
      </div>
      <span
        className="text-brand-400 transition group-hover:translate-x-1"
        aria-hidden
      >
        <Icon name="arrow-right" size={20} />
      </span>
    </Link>
  );
}
